// frontend/src/utils/assetQueryParams.js
/**
 * Utility functions for building and normalizing asset query parameters.
 * 
 * Ensures consistent parameter format for React Query cache keys and API calls.
 */

/**
 * Normalizes query parameters to ensure consistent query keys.
 * - Converts category codes to category__code__in format
 * - Ensures consistent key ordering
 * - Removes undefined/null values
 * 
 * @param {Object} params - Raw query parameters
 * @returns {Object} Normalized parameters
 */
export const normalizeAssetQueryParams = (params) => {
  const normalized = { ...params };
  
  // Remove undefined/null values
  Object.keys(normalized).forEach(key => {
    if (normalized[key] === undefined || normalized[key] === null || normalized[key] === '') {
      delete normalized[key];
    }
  });
  
  // If we have category__code__in, ensure category is not set
  if (normalized.category__code__in) {
    delete normalized.category;
  }
  
  // Sort keys for consistent query key generation
  const sortedKeys = Object.keys(normalized).sort();
  const sortedParams = {};
  sortedKeys.forEach(key => {
    sortedParams[key] = normalized[key];
  });
  
  return sortedParams;
};

/**
 * Builds asset query parameters from various sources.
 * Handles URL params, dropdown filters, and multi-select filters.
 * 
 * @param {Object} options
 * @param {URLSearchParams} options.searchParams - URL search parameters
 * @param {Object} options.filters - Multi-select filters object
 * @param {string} options.activeTab - Active tab filter
 * @param {string} options.searchQuery - Search query string
 * @param {number} options.currentPage - Current page number
 * @param {Array} options.categories - Available categories from API (for validation)
 * @returns {Object} Normalized query parameters
 */
export const buildAssetQueryParams = ({
  searchParams,
  filters,
  activeTab,
  searchQuery,
  currentPage,
  categories = [],
}) => {
  const params = {
    page: currentPage,
    page_size: 25, // PAGE_SIZE constant
  };
  
  // Add search query
  if (searchQuery && searchQuery.trim()) {
    params.search = searchQuery.trim();
  }
  
  // Tab filter (status)
  if (activeTab && activeTab !== 'all') {
    params.status = activeTab;
  }
  
  // Get URL params
  const urlStatus = searchParams.get('status');
  const urlCategory = searchParams.get('category');
  const urlCategoryCodes = searchParams.get('category__code__in');
  
  // Handle URL category params (priority: URL > dropdown > multi-select)
  if (urlCategoryCodes) {
    // Validate category codes against available categories
    const validCodes = urlCategoryCodes.split(',')
      .map(c => c.trim())
      .filter(code => {
        // Check against fetched categories
        return categories.some(cat => cat.code === code);
      });
    
    if (validCodes.length > 0) {
      params.category__code__in = validCodes.join(',');
    }
  } else if (urlCategory) {
    // Check if it's numeric (ID) or code
    if (/^\d+$/.test(urlCategory)) {
      // It's a category ID - validate it exists
      const categoryExists = categories.some(cat => cat.id === parseInt(urlCategory));
      if (categoryExists) {
        params.category = urlCategory;
      }
      // If invalid ID, don't set category param (will show all)
    } else {
      // It's a category code - convert to category__code__in format
      const validCode = categories.find(cat => cat.code === urlCategory);
      if (validCode) {
        params.category__code__in = urlCategory;
      }
      // If invalid code, don't set category param (will show all)
    }
  }
  
  // Handle URL status param (overrides tab if present)
  if (urlStatus) {
    params.status = urlStatus;
  }
  
  // Multi-select filters (override single selects when active)
  if (filters.categories && filters.categories.length > 0) {
    // Validate codes against available categories
    const validCodes = filters.categories.filter(code =>
      categories.some(cat => cat.code === code)
    );
    if (validCodes.length > 0) {
      params.category__code__in = validCodes.join(',');
      delete params.category; // Remove single category filter
    }
  }
  
  if (filters.statuses && filters.statuses.length > 0) {
    params.status__in = filters.statuses.join(',');
    delete params.status; // Remove single status filter
  }
  
  if (filters.departments && filters.departments.length > 0) {
    params.department__in = filters.departments.join(',');
  }

  if (filters.ownershipTypes && filters.ownershipTypes.length > 0) {
    params.ownership_type__in = filters.ownershipTypes.join(',');
  }
  
  return normalizeAssetQueryParams(params);
};

/**
 * Validates category code against available categories.
 * 
 * @param {string} code - Category code to validate
 * @param {Array} categories - Available categories from API
 * @returns {boolean} True if code is valid
 */
export const isValidCategoryCode = (code, categories) => {
  return categories.some(cat => cat.code === code);
};

/**
 * Validates category ID against available categories.
 * 
 * @param {string|number} id - Category ID to validate
 * @param {Array} categories - Available categories from API
 * @returns {boolean} True if ID is valid
 */
export const isValidCategoryId = (id, categories) => {
  if (!/^\d+$/.test(String(id))) {
    return false;
  }
  return categories.some(cat => cat.id === parseInt(id));
};
